import React from 'react';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MailIcon from '@mui/icons-material/Mail';
import { Box, Chip, Skeleton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { RecipientStatus as RecipientStatusEnum } from '@/types/roc/models/RecipientStatus';
import { getFromNowFormattedDate } from '@/utils/date';

import { type FacilityNameMap } from '../../hooks/useFacilityNamesQuery';
import {
  type OutboundReferralGridRow,
  REFERRAL_TYPE_LABELS,
  type RecipientStatus
} from '../../models/OutboundReferralModel';

import { ReferralStatusDropdown } from './ReferralStatusDropdown';

dayjs.extend(utc);

export function getRecipientStatusLabel(
  status: OutboundReferralGridRow['recipient_status']
) {
  const labels: Partial<
    Record<OutboundReferralGridRow['recipient_status'], string>
  > = {
    [RecipientStatusEnum.Pending]: 'Received',
    [RecipientStatusEnum.Accepted]: 'Accepted',
    [RecipientStatusEnum.NeedsMoreInfo]: 'Need More Info',
    // For now show confirmed as accepted as well. Eventually confirmed will be removed as a recipient status.
    [RecipientStatusEnum.Confirmed]: 'Accepted',
    [RecipientStatusEnum.Declined]: 'Declined',
    [RecipientStatusEnum.Closed]: 'Closed'
  };

  return labels[status] ?? status;
}

interface OutboundStatusChipProps {
  status: RecipientStatus;
}

export function OutboundStatusChip({ status }: OutboundStatusChipProps) {
  const theme = useTheme();

  const config: Partial<
    Record<
      RecipientStatus,
      {
        label: string;
        background: string;
        color: string;
        Icon?: React.ElementType;
      }
    >
  > = {
    [RecipientStatusEnum.Pending]: {
      label: 'Received',
      background: theme.palette.action.selected,
      color: theme.palette.text.primary
    },
    [RecipientStatusEnum.Accepted]: {
      label: 'Accepted',
      background: theme.palette.success.main,
      color: theme.palette.success.contrastText,
      Icon: CheckCircleIcon
    },
    [RecipientStatusEnum.NeedsMoreInfo]: {
      label: 'Need More Info',
      background: theme.palette.warning.main,
      color: theme.palette.warning.contrastText,
      Icon: HelpOutlineIcon
    },
    [RecipientStatusEnum.Confirmed]: {
      label: 'Accepted',
      background: theme.palette.success.main,
      color: theme.palette.success.contrastText,
      Icon: CheckCircleIcon
    },
    [RecipientStatusEnum.Declined]: {
      label: 'Declined',
      background: theme.palette.error.main,
      color: theme.palette.error.contrastText,
      Icon: CancelOutlinedIcon
    },
    [RecipientStatusEnum.Closed]: {
      label: 'Closed',
      background: theme.palette.error.main,
      color: theme.palette.error.contrastText,
      Icon: CancelOutlinedIcon
    }
  };

  const chipConfig = config[status];
  if (!chipConfig) {
    return null;
  }
  const { label, background, color, Icon } = chipConfig;

  return (
    <Chip
      label={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            color
          }}
        >
          {Icon && <Icon sx={{ fontSize: 15 }} />}
          {label}
        </Box>
      }
      sx={{
        backgroundColor: background,
        '& .MuiChip-label': { px: 1.25 }
      }}
    />
  );
}

export function getRecipientFacilityName(
  recipientFacilityId: string,
  facilityMap: FacilityNameMap
) {
  if (!recipientFacilityId) {
    return '—';
  }

  return facilityMap[recipientFacilityId] ?? '—';
}

export function getOutboundDataGridColumns(
  facilityMap: FacilityNameMap,
  isAllFacilitiesSelected = false,
  onFacilityClick?: (facilityId: string) => void
): GridColDef<OutboundReferralGridRow>[] {
  return [
    {
      field: 'resident',
      headerName: 'Resident',
      filterable: false,
      flex: 1.5,
      minWidth: 160,
      valueGetter: ({ row }: { row: OutboundReferralGridRow }) =>
        [row.first_name, row.last_name].filter(Boolean).join(' '),
      renderCell: (params: GridRenderCellParams<OutboundReferralGridRow>) => (
        <Stack direction="row" alignItems="center" gap={0.75}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, fontSize: 13.5, color: 'secondary.main' }}
          >
            {params.value || '—'}
          </Typography>
          {params.row.has_unread && (
            <MailIcon
              aria-label="Unread message"
              data-testid="outbound-row-unread-mail"
              sx={{ fontSize: 16, color: 'primary.main' }}
            />
          )}
        </Stack>
      )
    },
    ...(isAllFacilitiesSelected
      ? [
          {
            field: 'sender_facility_id',
            headerName: 'Facility',
            filterable: false,
            flex: 1.3,
            minWidth: 150,
            valueGetter: ({ row }: { row: OutboundReferralGridRow }) =>
              facilityMap[row.sender_facility_id] ?? '',
            renderCell: (
              params: GridRenderCellParams<OutboundReferralGridRow>
            ) => {
              const name = facilityMap[params.row.sender_facility_id];
              if (name === undefined) {
                return <Skeleton width={100} />;
              }
              return <Typography variant="body2">{name ?? '—'}</Typography>;
            }
          }
        ]
      : []),
    {
      field: 'referral_type',
      headerName: 'Referral Type',
      sortable: false,
      flex: 0.9,
      minWidth: 110,
      valueGetter: ({ row }: { row: OutboundReferralGridRow }) =>
        REFERRAL_TYPE_LABELS[row.referral_type] ?? row.referral_type,
      renderCell: (params: GridRenderCellParams<OutboundReferralGridRow>) => (
        <Typography variant="body2" sx={{ color: 'text.primary' }}>
          {REFERRAL_TYPE_LABELS[params.row.referral_type] ??
            params.row.referral_type}
        </Typography>
      )
    },
    {
      field: 'target_discharge_date',
      headerName: 'Move-Out',
      filterable: false,
      flex: 0.9,
      minWidth: 110,
      renderCell: (params: GridRenderCellParams<OutboundReferralGridRow>) => (
        <Typography variant="body2" sx={{ color: 'text.primary' }}>
          {params.row.target_discharge_date
            ? dayjs.utc(params.row.target_discharge_date).format('MMM D, YYYY')
            : '—'}
        </Typography>
      )
    },
    {
      field: 'recipient_facility_id',
      headerName: 'Recipient',
      sortable: false,
      flex: 1,
      minWidth: 150,
      valueGetter: ({ row }: { row: OutboundReferralGridRow }) =>
        row.recipient_facility_name ??
        facilityMap[row.recipient_facility_id] ??
        '',
      renderCell: (params: GridRenderCellParams<OutboundReferralGridRow>) => {
        const { recipient_facility_name, recipient_facility_id } = params.row;
        const hasName = Boolean(
          recipient_facility_name?.trim() ||
            facilityMap[recipient_facility_id]?.trim()
        );
        const isClickable = Boolean(
          onFacilityClick && recipient_facility_id && hasName
        );
        if (recipient_facility_name) {
          return (
            <Typography
              variant="body2"
              sx={{
                color: isClickable ? 'primary.main' : 'text.primary',
                cursor: isClickable ? 'pointer' : undefined,
                '&:hover': isClickable ? { textDecoration: 'underline' } : {}
              }}
              onClick={
                isClickable
                  ? (e) => {
                      e.stopPropagation();
                      onFacilityClick!(recipient_facility_id);
                    }
                  : undefined
              }
            >
              {recipient_facility_name}
            </Typography>
          );
        }
        if (
          recipient_facility_id &&
          facilityMap[recipient_facility_id] === undefined
        ) {
          return <Skeleton width={100} />;
        }
        return (
          <Typography
            variant="body2"
            sx={{
              color: isClickable ? 'primary.main' : 'text.primary',
              cursor: isClickable ? 'pointer' : undefined,
              '&:hover': isClickable ? { textDecoration: 'underline' } : {}
            }}
            onClick={
              isClickable
                ? (e) => {
                    e.stopPropagation();
                    onFacilityClick!(recipient_facility_id);
                  }
                : undefined
            }
          >
            {getRecipientFacilityName(recipient_facility_id, facilityMap)}
          </Typography>
        );
      }
    },
    {
      field: 'recipient_status',
      headerName: 'Recipient Status',
      sortable: false,
      flex: 1,
      minWidth: 130,
      valueGetter: ({ row }: { row: OutboundReferralGridRow }) =>
        row.recipient_facility_id
          ? getRecipientStatusLabel(row.recipient_status)
          : '',
      renderCell: (params: GridRenderCellParams<OutboundReferralGridRow>) => {
        if (!params.row.recipient_facility_id) {
          return <Typography variant="body2">—</Typography>;
        }

        return <OutboundStatusChip status={params.row.recipient_status} />;
      }
    },
    {
      field: 'last_activity_at',
      headerName: 'Last Activity',
      filterable: false,
      flex: 1,
      minWidth: 140,
      renderCell: (params: GridRenderCellParams<OutboundReferralGridRow>) => {
        const { last_activity_at } = params.row;
        if (!last_activity_at) {
          return <Typography variant="body2">—</Typography>;
        }
        return (
          <Stack gap={0.25}>
            <Typography
              variant="body2"
              sx={{
                fontSize: 12.5,
                color: 'secondary.main',
                lineHeight: 1.4
              }}
            >
              {getFromNowFormattedDate(last_activity_at)}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: 11.5, lineHeight: 1.4 }}
            >
              {dayjs.utc(last_activity_at).local().format('MMM D, h:mm A')}
            </Typography>
          </Stack>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Referral Status',
      width: 160,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<OutboundReferralGridRow>) => (
        <ReferralStatusDropdown
          referral={{
            referral_id: params.row.referral_id,
            status: params.row.referral_status,
            first_name: params.row.first_name,
            last_name: params.row.last_name,
            recipients: params.row.recipient_facility_id
              ? [
                  {
                    facility_id: params.row.recipient_facility_id,
                    status: params.row.recipient_status,
                    facility_name: params.row.recipient_facility_name
                  }
                ]
              : []
          }}
        />
      )
    }
  ];
}
